import { useParameterStore, ValidationStatus } from "@/frontend/store/ParameterStore";
import { CompleteState } from "@/frontend/SVGs/CompleteState";
import { I18n } from "@/i18n/I18n";
import { ParameterType, ParameterSubjectType } from "@/types/parameterTypes";
import { Parameter } from "@/backend/models/parameter";
import { useMemo, useState, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useShallow } from "zustand/react/shallow";
import { CollapseButton } from "../buttons/CollapseButton";
import { ValidationsFailedIcon } from "../validations/ValidationsFailedIcon";
import { ValidationsOverriddenIcon } from "../validations/ValidationsOverriddenIcon";
import { useSubjectIsDisabled } from "@/frontend/hooks/useSubjectIsDisabled";
import { ParameterRunEditModal } from "./ParameterRunEditModal";
import { ParameterItemRunView } from "./ParameterItemRunView";
import { ParameterIsEmptyIcon } from "./ParameterIsEmptyIcon";

export function ParameterRun(props: {id: string, parameterType: ParameterType, parentType: ParameterSubjectType}) {
  const { id, parameterType, parentType } = props;
  const parameters = useParameterStore(useShallow((state) => state.parametersForSubjectAndType(id, parameterType)));
  const validationStatuses = useParameterStore(useShallow(state => parameters.map(p => state.getValidationStatusesForSubject(p.id)).flat()));
  const hasEmptyParams = hasEmptyParameters(parameters);
  const parentDisabled = useSubjectIsDisabled(id, parentType);

  const editableParameters = useMemo(() => parameters.filter((param) => !param.viewOnly), [parameters]);
  const allValidationsPassingOrNone = validationStatuses.every(v => v === ValidationStatus.PASSING || v === ValidationStatus.NONE);
  const someValidationsFailing = validationStatuses.some(v => v === ValidationStatus.FAILING);
  const someValidationsOverridden = validationStatuses.some(v => v === ValidationStatus.OVERIDDEN);

  const [showAll, setShowAll] = useState(
    (hasEmptyParams && editableParameters.length > 0) ||
    someValidationsFailing
  );

  useEffect(() => {
    if (someValidationsFailing) {
      setShowAll(true);
    }
    if (hasEmptyParams && editableParameters.length > 0) {
      setShowAll(true);
    }
  }, [someValidationsFailing, hasEmptyParams, editableParameters.length]);

  if (parameters.length === 0) {
    return null;
  }

  return (
    <div className='card'>
      { !showAll && parameters.map(parameter => <div id={parameter.id} key={parameter.id} />) }
      <div className='card-header d-flex justify-content-between align-items-center'>
        <div className='d-flex align-items-center gap-2'>
          <div className='d-flex align-items-center gap-2'>
            <CollapseButton showElement={showAll} setShowElement={setShowAll} title={I18n.get('typeParameters', { type: I18n.get(parameterType) })}/>
            {I18n.get('typeParameters', { type: I18n.get(parameterType) })}
          </div>
          {(!hasEmptyParams && allValidationsPassingOrNone) && <CompletedParameters />}
          {someValidationsFailing && <ValidationsFailedIcon placement='right' />}
          {!someValidationsFailing && someValidationsOverridden && <ValidationsOverriddenIcon placement='right' />}
          {(hasEmptyParams && editableParameters.length > 0) && <ParameterIsEmptyIcon placement='right' parameterType={parameterType} />}
        </div>
        { (!parentDisabled) && <ParameterRunEditModal subjectId={id} parameterType={parameterType} buttonLabel={I18n.get('edit')} /> }
      </div>
      { (showAll) && <div className='card-body d-flex flex-column gap-2'>
        <NonCollapsibleParameterList parameters={parameters} />
      </div> }
    </div>
  );
}

function NonCollapsibleParameterList(props: { parameters: Parameter[] }) {
  const { parameters } = props;

  if (parameters.length === 0) return null;

  return (
    <div className='pb-3 px-3'>
      { parameters.map((parameter, index) => (
        <div id={parameter.id} key={parameter.id}>
          <ParameterItemRunView parameter={parameter}/>
          {index < parameters.length - 1 && <hr />}
        </div>
      ))}
    </div>
  );
}

function CompletedParameters() {
  return (
    <OverlayTrigger
      placement='right'
      overlay={<Tooltip>
        {I18n.get('parametersCompleted')}
      </Tooltip>}
    >
    <div>
      <CompleteState />
    </div>
    </OverlayTrigger>
  );
}

function hasEmptyParameters(parameters: Parameter[]) {
  return parameters.some(param => !param.viewOnly && param.hasEmptyField());
}
